/**
 * Phase 2.9.4 — Question-specific Telecom Service Model evidence examples.
 *
 * Resolves "Additional Evidence for {Service Model}" guidance based on BOTH:
 *   1. The selected Telecom Service Model(s) (from the parent portfolio)
 *   2. The specific NIST CSF 2.0 control being answered
 *
 * Keyed by NIST control code with progressive fallback:
 *   full code  (e.g. "PR.AA-01")
 *   → category (e.g. "PR.AA")
 *   → function (e.g. "PR")
 *
 * If no question-specific items are found for a given service model, the
 * resolver falls back to the broad per-service-model list from
 * `serviceModelEvidenceExamples.ts`.
 *
 * Scope: telecom_v2 respondent questionnaire only.
 * Does NOT affect scoring, NIST calculations, reviewer console, findings,
 * or report generation.
 */

import { SERVICE_MODEL_EVIDENCE_EXAMPLES } from "./serviceModelEvidenceExamples";

type ModelEvidenceMap = Record<string, string[]>;

/**
 * control-code-or-prefix → service-model → evidence items
 */
export const SERVICE_MODEL_QUESTION_EVIDENCE: Record<string, ModelEvidenceMap> = {
  // ──────────────── GOVERN (GV.*) ────────────────
  GV: {
    MNO: [
      "Board-approved cybersecurity governance charter covering RAN/Core/IMS",
      "Telecom regulator (e.g. national NRA) compliance attestations",
      "Cyber risk register entries for signaling, roaming, and lawful intercept",
      "Vendor (RAN/Core OEM) security oversight committee minutes",
    ],
    MVNO: [
      "Host-MNO security oversight and interconnect risk register",
      "BSS/OSS supplier governance and contract security exhibits",
      "Subscriber data protection policy aligned to host-MNO obligations",
    ],
    ISP: [
      "BGP/peering security governance documentation (RPKI, MANRS commitments)",
      "Customer-premises CPE lifecycle and security policy",
      "DDoS mitigation governance and upstream provider SLAs",
    ],
    FiberCo: [
      "Transport-network risk register (DWDM/OTN, route diversity)",
      "Cable landing station and POP physical security governance",
      "Wholesale customer security obligation registers",
    ],
    TowerCo: [
      "Tower-site physical security governance policy",
      "Tenant/operator security obligation register",
      "Power and environmental risk governance documentation",
    ],
    "Wholesale Carrier": [
      "International interconnect and signaling risk register",
      "IPX/peering partner security governance",
      "Cross-border data and lawful intercept compliance documentation",
    ],
  },

  // ──────────────── ASSET MANAGEMENT (ID.AM) ────────────────
  "ID.AM": {
    MNO: [
      "RAN inventory (gNodeB / eNodeB) with firmware versions",
      "Core network function inventory (AMF, SMF, UPF, HSS/UDM)",
      "Signaling/transport element inventory (STP, DRA, SBC)",
      "Subscriber and roaming data-flow diagrams",
    ],
    MVNO: [
      "BSS/billing and provisioning asset inventory",
      "Host-MNO API and interconnect inventory",
      "Subscriber data store (HSS/UDM tenancy) inventory",
    ],
    ISP: [
      "BGP router and edge device inventory",
      "CPE fleet inventory and firmware register",
      "Customer service data-flow diagrams",
    ],
    FiberCo: [
      "DWDM/OTN/ROADM equipment inventory",
      "Fiber route and POP asset register",
      "Wholesale-circuit data-flow diagrams",
    ],
    TowerCo: [
      "Tower-site equipment and power-system asset register",
      "Tenant equipment inventory and segregation records",
      "Environmental monitoring sensor inventory",
    ],
    "Wholesale Carrier": [
      "Backbone router and signaling gateway inventory",
      "IPX/interconnect circuit inventory",
      "International transit data-flow diagrams",
    ],
  },

  // ──────────────── RISK ASSESSMENT / VULN (ID.RA) ────────────────
  "ID.RA": {
    MNO: [
      "Threat intel feeds for SS7/Diameter/GTP signaling abuse",
      "RAN/Core vulnerability scan reports (OEM advisories tracked)",
      "Subscriber-fraud and SIM-swap risk assessments",
      "Roaming-partner threat assessment records",
    ],
    MVNO: [
      "Host-MNO vulnerability disclosure intake records",
      "BSS/billing platform vulnerability scan reports",
      "Subscriber-fraud risk assessment",
    ],
    ISP: [
      "BGP hijack/route-leak monitoring evidence (RPKI ROV results)",
      "CPE firmware vulnerability advisories tracked",
      "DDoS threat landscape and tabletop results",
    ],
    FiberCo: [
      "Physical-layer threat assessment (fiber cuts, tap risk)",
      "DWDM/OTN OEM vulnerability advisories tracked",
      "Landing-station physical-threat intelligence reports",
    ],
    TowerCo: [
      "Site-intrusion and theft threat assessments",
      "Environmental/power threat assessments",
      "Tenant-introduced risk assessments",
    ],
    "Wholesale Carrier": [
      "Signaling firewall threat intel and SS7/Diameter test reports",
      "Interconnect partner due-diligence and risk assessments",
      "Submarine-cable / international transit threat assessments",
    ],
  },

  // ──────────────── IDENTITY & ACCESS CONTROL (PR.AA) ────────────────
  "PR.AA": {
    MNO: [
      "RAN OAM privileged access logs",
      "Core network PAM session recordings (5G SBA / EPC / IMS)",
      "Vendor remote-access approval tickets and MFA records",
      "Cell-site jump-host MFA evidence",
    ],
    MVNO: [
      "BSS/CRM privileged access reviews",
      "Host-MNO API credential vault and rotation records",
      "Subscriber-data (HSS/UDM) admin access logs",
    ],
    ISP: [
      "Edge/BGP router admin access logs and MFA enforcement",
      "Customer-portal admin role review",
      "NOC privileged access and break-glass procedures",
    ],
    FiberCo: [
      "POP / meet-me-room physical and logical access logs",
      "NOC privileged access records",
      "Field technician access approvals and badge logs",
      "Optical transport management system access reviews",
    ],
    TowerCo: [
      "Tower-site badge/biometric access logs and CCTV review",
      "Climbing-crew access approvals and escort records",
      "Tenant access segregation evidence",
    ],
    "Wholesale Carrier": [
      "Backbone router admin access logs and MFA enforcement",
      "Signaling firewall admin access reviews",
      "Interconnect-partner federated identity records",
    ],
  },

  // ──────────────── NETWORK SEGMENTATION (PR.IR) ────────────────
  "PR.IR": {
    MNO: [
      "RAN ↔ Core network segmentation diagrams",
      "Signaling plane (SS7/Diameter/GTP) firewall policies",
      "Roaming/IPX segregation evidence",
      "OAM/management plane VRF or VLAN separation evidence",
    ],
    MVNO: [
      "Tenant isolation evidence on host-MNO platform",
      "BSS ↔ host-MNO interconnect segmentation",
    ],
    ISP: [
      "Customer ↔ management plane segmentation",
      "Peering edge ↔ core segmentation policies",
      "DDoS scrubbing path segmentation",
    ],
    FiberCo: [
      "Transport ↔ management network segmentation",
      "Wholesale tenant logical segregation evidence (VLAN/VRF/OTN)",
      "POP / meet-me-room cross-connect controls",
    ],
    TowerCo: [
      "Tenant equipment physical/logical segregation evidence",
      "Site management network isolation diagrams",
    ],
    "Wholesale Carrier": [
      "Customer-VRF separation evidence on backbone",
      "Signaling-firewall segmentation between interconnect partners",
      "IPX peer segmentation policies",
    ],
  },

  // ──────────────── SECURE CONFIG / HARDENING / CHANGE / PATCH (PR.PS) ────────────────
  "PR.PS": {
    MNO: [
      "RAN configuration baselines (gNodeB / eNodeB)",
      "Core network function hardening guides and CIS-style baselines",
      "Vendor-coordinated change tickets and rollback evidence",
      "Patch SLA reports for Core/IMS/Signaling elements",
    ],
    MVNO: [
      "BSS/OSS hardening baselines",
      "Patch management reports for billing/CRM platforms",
      "Change advisory board records covering host-MNO impacting changes",
    ],
    ISP: [
      "Edge/BGP router hardening baselines",
      "CPE firmware management and patch evidence",
      "Change tickets for peering and customer-impacting changes",
    ],
    FiberCo: [
      "DWDM/OTN element hardening baselines",
      "Management-plane patch records",
      "Optical change-window approvals and rollback plans",
    ],
    TowerCo: [
      "Site equipment configuration baselines (controllers, BMS)",
      "Firmware update records for environmental/power systems",
      "Maintenance change approvals",
    ],
    "Wholesale Carrier": [
      "Backbone router hardening baselines",
      "Signaling firewall configuration baselines and change logs",
      "Patch SLA reports for interconnect-facing systems",
    ],
  },

  // ──────────────── DATA / ENCRYPTION / COMMS (PR.DS) ────────────────
  "PR.DS": {
    MNO: [
      "Air-interface encryption configuration (5G NEA / 4G EEA)",
      "IPsec tunnel inventory for S1 / N2 / N3 / Xn",
      "Core SBI mTLS configuration evidence",
      "Subscriber database (HSS/UDM) encryption at rest evidence",
    ],
    MVNO: [
      "API/TLS configuration for host-MNO interfaces",
      "Subscriber data encryption-at-rest evidence",
      "Key management procedures for billing data",
    ],
    ISP: [
      "Customer traffic encryption options (IPsec / MACsec where applicable)",
      "BGP session security (TCP-AO / GTSM) configuration",
      "TLS configuration for customer portals",
    ],
    FiberCo: [
      "MACsec / IPsec configuration for backhaul circuits",
      "DWDM / OTN encryption settings (OTNsec / layer-1 encryption)",
      "Key management procedures for transport encryption",
      "Fiber route protection documentation",
    ],
    TowerCo: [
      "Management-plane TLS/VPN configuration",
      "Tenant traffic isolation/encryption obligations",
    ],
    "Wholesale Carrier": [
      "Backbone MACsec / IPsec deployment evidence",
      "Signaling encryption (Diameter TLS, SEPP for 5G roaming) configuration",
      "Key lifecycle and HSM evidence for transport encryption",
    ],
  },

  // ──────────────── AWARENESS & TRAINING (PR.AT) ────────────────
  "PR.AT": {
    MNO: [
      "Role-based training records for RAN/Core engineers",
      "Signaling-fraud and SIM-swap awareness training records",
      "Vendor on-site staff security briefing logs",
    ],
    MVNO: [
      "Customer-service staff fraud-awareness training records",
      "Subscriber-data handling training evidence",
    ],
    ISP: [
      "NOC staff incident-handling training",
      "DDoS playbook training and drill records",
    ],
    FiberCo: [
      "Field-technician security training records",
      "POP / landing-station physical-security briefings",
    ],
    TowerCo: [
      "Climbing-crew and field-tech security briefings",
      "Tenant-escort training records",
    ],
    "Wholesale Carrier": [
      "Interconnect/peering engineer security training",
      "Signaling-abuse awareness training records",
    ],
  },

  // ──────────────── MONITORING (DE.CM) ────────────────
  "DE.CM": {
    MNO: [
      "RAN/Core SIEM coverage and log-source inventory",
      "Signaling firewall (SS7/Diameter/GTP) monitoring alerts",
      "Subscriber-fraud monitoring dashboards",
      "Roaming-anomaly detection evidence",
    ],
    MVNO: [
      "BSS/CRM monitoring dashboards",
      "Host-MNO API monitoring and rate-limit alerts",
    ],
    ISP: [
      "BGP route-monitoring evidence (BGPmon/RIPE NCC)",
      "DDoS detection telemetry and NetFlow analytics",
      "CPE telemetry monitoring (where applicable)",
    ],
    FiberCo: [
      "Optical performance and tap-detection monitoring",
      "POP environmental/physical intrusion monitoring",
      "Transport-network NMS alerting evidence",
    ],
    TowerCo: [
      "Site CCTV / intrusion-detection monitoring evidence",
      "Power/generator/fuel telemetry monitoring",
      "Environmental monitoring (temperature, humidity, door) alerts",
    ],
    "Wholesale Carrier": [
      "Backbone NetFlow / SNMP / streaming-telemetry monitoring",
      "Signaling firewall alert evidence",
      "Peering anomaly detection reports",
    ],
  },

  // ──────────────── EVENT ANALYSIS / DETECTION (DE.AE) ────────────────
  "DE.AE": {
    MNO: [
      "SOC use cases tuned for RAN/Core/Signaling",
      "Subscriber-fraud correlation rules and alerts",
      "Roaming-anomaly correlation evidence",
    ],
    MVNO: [
      "BSS/billing anomaly detection rules",
      "Host-MNO API misuse correlation evidence",
    ],
    ISP: [
      "BGP-hijack / route-leak detection runbooks and alerts",
      "DDoS attack-pattern correlation evidence",
    ],
    FiberCo: [
      "Optical-anomaly correlation evidence",
      "Physical-intrusion alert correlation with NMS data",
    ],
    TowerCo: [
      "Multi-sensor site-intrusion correlation evidence",
      "Power-anomaly correlation with site access logs",
    ],
    "Wholesale Carrier": [
      "Signaling-abuse detection use cases",
      "Cross-domain (signaling + transport) correlation evidence",
    ],
  },

  // ──────────────── RESPOND (RS.*) ────────────────
  RS: {
    MNO: [
      "IR runbooks for RAN/Core/Signaling incidents",
      "Subscriber-fraud and SIM-swap response procedures",
      "Regulator/NRA notification templates and timelines",
      "Vendor coordination procedures during major incidents",
    ],
    MVNO: [
      "Host-MNO joint IR procedures",
      "Subscriber-fraud response playbooks",
      "Regulator notification templates",
    ],
    ISP: [
      "DDoS response runbooks and scrubbing activation evidence",
      "BGP-hijack response procedures",
      "Customer-notification templates",
    ],
    FiberCo: [
      "Fiber-cut response runbooks and restoration SLAs",
      "Landing-station physical-incident response procedures",
      "Wholesale-customer notification templates",
    ],
    TowerCo: [
      "Site-intrusion response procedures",
      "Power-outage / generator-failure response runbooks",
      "Tenant notification templates",
    ],
    "Wholesale Carrier": [
      "Signaling-incident response runbooks",
      "Interconnect-partner coordination procedures",
      "Cross-border notification and regulator escalation templates",
    ],
  },

  // ──────────────── RECOVER (RC.*) ────────────────
  RC: {
    MNO: [
      "RAN failover and Core geo-redundancy architecture",
      "Signaling redundancy diagrams (SS7/Diameter/SEPP)",
      "DR test results for voice/data/messaging services",
      "Subscriber-database (HSS/UDM) recovery test evidence",
    ],
    MVNO: [
      "Host-MNO failover dependency documentation",
      "BSS/billing DR test evidence",
      "Subscriber-data restore drills",
    ],
    ISP: [
      "Peering/transit diversity evidence",
      "Edge-router failover test results",
      "CPE/firmware rollback procedures",
    ],
    FiberCo: [
      "Fiber route diversity diagrams",
      "Restoration SLA evidence and historical MTTR",
      "Fiber-cut response and re-splicing procedures",
      "Alternate carrier / diverse path contracts",
    ],
    TowerCo: [
      "Backup-power runtime and refuelling SLAs",
      "Generator/UPS DR test results",
      "Site-restoration runbooks",
    ],
    "Wholesale Carrier": [
      "Backbone redundancy and protection-switching evidence",
      "Submarine-cable diversity documentation",
      "Signaling failover DR test results",
    ],
  },
};

/**
 * Resolve question-specific service-model evidence groups.
 *
 * Lookup order per (control, model):
 *   1. exact control code (e.g. "PR.AA-01")
 *   2. category prefix    (e.g. "PR.AA")
 *   3. function prefix    (e.g. "PR")
 *
 * If no question-specific items are found for a service model across any of
 * its question's controls, falls back to the broad
 * `SERVICE_MODEL_EVIDENCE_EXAMPLES` list for that model.
 *
 * Returns empty array when no models supplied.
 */
export function resolveServiceModelEvidenceForQuestion(
  controls: string[] | null | undefined,
  models: string[] | null | undefined,
): { model: string; items: string[] }[] {
  if (!models || models.length === 0) return [];
  const ctrls = (controls ?? []).map((c) => String(c).trim()).filter(Boolean);

  const groups: { model: string; items: string[] }[] = [];

  for (const model of models) {
    const seen = new Set<string>();
    const items: string[] = [];

    for (const code of ctrls) {
      const upper = code.toUpperCase();
      const category = upper.includes("-") ? upper.split("-")[0] : upper; // e.g. PR.AA
      const fn = category.includes(".") ? category.split(".")[0] : category; // e.g. PR
      const candidates = [upper, category, fn];

      for (const cand of candidates) {
        const list = SERVICE_MODEL_QUESTION_EVIDENCE[cand]?.[model];
        if (list && list.length > 0) {
          for (const it of list) {
            const k = it.trim().toLowerCase();
            if (!seen.has(k)) {
              seen.add(k);
              items.push(it);
            }
          }
          break; // most-specific match wins for this control
        }
      }
    }

    // Fallback to broad per-model bucket if no question-specific items found
    if (items.length === 0) {
      const broad = SERVICE_MODEL_EVIDENCE_EXAMPLES[model] ?? [];
      for (const it of broad) {
        const k = it.trim().toLowerCase();
        if (!seen.has(k)) {
          seen.add(k);
          items.push(it);
        }
      }
    }

    if (items.length > 0) groups.push({ model, items });
  }

  return groups;
}
